import { message, Popover, Tag } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { IconHelp, IconCamera, IconCopy } from "@icons/index";
import ShotDemo from "./shotDemo.png";
import classNames from "classnames";
import { useEffect, useRef } from "react";
import { themeActionAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import "./index.scss";

const shotDemoList = [
  { title: "Wide Shot", bgPosition: "0px 0px" },
  { title: "Full Shot", bgPosition: "-140px 0px" },
  { title: "Medium Shot", bgPosition: "-280px 0px" },
  { title: "Medium Close-Up", bgPosition: "-420px 0px" },
  { title: "Close-Up Shot", bgPosition: "-560px 0px" },
  { title: "Extreme Close-Up Shot", bgPosition: "-700px 0px" },
];

const TIMEOUT_INTERVAL = 5000;

interface StoryboardTableProps {
  videoScripts: any[];
  isTranslate?: boolean;
  scriptText?: any;
  isPlaying?: boolean;
  handleCurTime?: (time: number) => void;
}

const StoryboardTable = ({ videoScripts, isTranslate, scriptText, isPlaying, handleCurTime }: StoryboardTableProps) => {
  const isDark = useAtomValue(themeActionAtom) === "dark";
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyAudioScript = () => {
    const texts = videoScripts
      ?.map((row: any) => {
        if (Array.isArray(row.audio_script)) {
          return row.audio_script
            .map((line: any) => (isTranslate ? line.translate_script : line.script) || "")
            .filter(Boolean)
            .join("\n");
        }
        return (isTranslate ? row.translate_audio_script : row.audio_script) || "";
      })
      .filter(Boolean);
    navigator.clipboard.writeText(texts.join("\n\n")).then(() => {
      message.success(t("Copy succeed"));
    });
  };

  // 滚动到当前激活的脚本行
  const scrollToActive = () => {
    scrollContainerRef.current
      ?.querySelector<HTMLElement>(".video-script-active")
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // 启动 3 秒无操作倒计时，到期后自动滚回激活项
  const startInactivityTimer = () => {
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      inactivityTimerRef.current = null;
      scrollToActive();
    }, TIMEOUT_INTERVAL);
  };

  // scriptText 变化时自动滚动（inactivityTimerRef 有值说明用户正在操作，跳过）
  useEffect(() => {
    if (!inactivityTimerRef.current) scrollToActive();
  }, [scriptText?.start_time, scriptText?.end_time]);

  // 用户交互检测（wheel/mousedown/keydown/touchstart 仅用户触发，排除程序 scroll）
  useEffect(() => {
    const panel = scrollContainerRef.current;
    if (!panel) return;
    const events = ["wheel", "mousedown", "keydown", "touchstart"] as const;
    events.forEach((ev) => panel.addEventListener(ev, startInactivityTimer));
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      events.forEach((ev) => panel.removeEventListener(ev, startInactivityTimer));
    };
  }, []);

  // 暂停/播放视频时重置计时器
  useEffect(() => {
    if (inactivityTimerRef.current) startInactivityTimer();
  }, [isPlaying]);

  return (
    <div
      ref={scrollContainerRef}
      className={classNames(
        "StoryboardTable overflow-y-scroll h-full p-[16px] overflow-auto",
        location.pathname === "/script-review" && "pt-0",
      )}
    >
      <div className="border border-kds-border rounded-[8px] overflow-hidden">
        <div className="grid grid-cols-[240px_1fr_1fr] bg-kds-bg-soft text-kds-fg text-[13px] font-medium">
          <div className="p-[12px]">
            <Trans>Scene</Trans>
          </div>
          <div className="p-[12px] flex items-center gap-[4px]">
            <Trans>Visual Description</Trans>
            {location.pathname === "/insight" && (
              <Popover
                content={
                  <div className="grid grid-cols-3 gap-y-[6px] gap-x-[4px] p-[8px]">
                    {shotDemoList.map((item) => (
                      <div key={item.title} className="w-[140px] flex flex-col items-center">
                        <div
                          className="w-[140px] h-[130px] bg-gray-100"
                          style={{
                            backgroundImage: `url(${ShotDemo})`,
                            backgroundPosition: item.bgPosition,
                            backgroundSize: "840px 130px",
                            backgroundRepeat: "no-repeat",
                          }}
                        />
                        <span className="font-semibold text-kds-fg mt-[6px] text-[13px] whitespace-nowrap">
                          <Trans>{item.title}</Trans>
                        </span>
                      </div>
                    ))}
                  </div>
                }
                trigger="click"
                placement="bottom"
              >
                <span>
                  <IconHelp className="help_icon cursor-pointer" />
                </span>
              </Popover>
            )}
          </div>
          <div className="p-[12px] relative">
            <Trans>Audio Script</Trans>
            <div
              className="absolute right-[12px] top-[12px] cursor-pointer flex items-center gap-[4px] text-kds-accent-fg"
              onClick={handleCopyAudioScript}
            >
              <IconCopy className="w-[16px] h-[16px] **:stroke-current" />
              <Trans>Copy</Trans>
            </div>
          </div>
        </div>
        {videoScripts?.map((row: any, idx: number) => (
          <div key={idx} className="grid grid-cols-[240px_1fr_1fr] text-[14px] border-t border-kds-border">
            {/* Scene */}
            <div className="flex flex-col items-baseline gap-[8px] p-[12px] min-w-0">
              <div className="flex items-center gap-[8px]">
                <span
                  className={classNames(
                    "inline-block w-2 h-2 rounded-full",
                    isDark ? "kalo-bg-linear-gray" : "bg-kds-accent-fg",
                  )}
                />
                <span className="font-semibold text-kds-fg flex-1">
                  <Trans>{row.scene}</Trans>
                </span>
              </div>
              {row.start_time != null && row.end_time != null && (
                <Tag className="rounded-full!" color="processing">
                  {row.start_time}~{row.end_time}s
                </Tag>
              )}
            </div>
            {/* Visual Description */}
            <div className="flex flex-col gap-[8px] p-[12px] mr-[4px] min-w-0">
              {row.shot_scale && (
                <div className="flex gap-[4px] items-center text-kds-fg-soft text-[14px] font-semibold w-fit">
                  <IconCamera className="w-[16px] h-[16px]" />
                  <Trans>{row.shot_scale}</Trans>
                </div>
              )}
              <div className="text-kds-fg text-[14px] leading-[22px] wrap-break-word font-normal">
                {row.visual_description}
              </div>
              {isTranslate && (
                <div className="text-kds-fg-subtle text-[14px] leading-[22px] wrap-break-word mt-1">
                  {row.translate_visual_description}
                </div>
              )}
            </div>
            {/* Audio Script */}
            <div className="flex flex-col gap-[4px] px-[8px] p-[10px] min-w-0">
              {Array.isArray(row.audio_script) ? (
                <>
                  {/* 脚本导出 */}
                  {row.audio_script.length > 0
                    ? row.audio_script.map((line: any, i: number) => {
                        const isActive = scriptText?.script === line?.script;
                        return (
                          <div
                            key={`script-${line.start_time}-${line.end_time}-${i}`}
                            id={`script-${line.start_time}-${line.end_time}`}
                          >
                            <p
                              key={i + "-en"}
                              className={classNames(
                                "mb-0 leading-[22px] wrap-break-word text-kds-fg text-[14px] font-normal py-[2px] px-[4px]",
                                isActive && !isTranslate && "video-script-active",
                              )}
                              style={{ cursor: handleCurTime ? "pointer" : undefined }}
                              onClick={() => handleCurTime && handleCurTime(line.start_time)}
                            >
                              {line.script || "-"}
                            </p>
                            {isTranslate && (
                              <p
                                key={i + "-trans"}
                                className={classNames(
                                  "mb-0 leading-[22px] wrap-break-word text-kds-fg-subtle text-[14px] py-[2px] px-[4px]",
                                  isActive && isTranslate && "video-script-active",
                                )}
                                style={{ cursor: handleCurTime ? "pointer" : undefined }}
                                onClick={() => handleCurTime && handleCurTime(line.start_time)}
                              >
                                {line.translate_script || "-"}
                              </p>
                            )}
                          </div>
                        );
                      })
                    : "-"}
                </>
              ) : (
                <>
                  {/* 脚本重写 */}
                  <p className="mb-0 leading-[22px] wrap-break-word text-kds-fg">{row.audio_script || "-"}</p>
                  {isTranslate && (
                    <p className="mb-0 leading-[22px] wrap-break-word text-kds-fg-subtle">
                      {row.translate_audio_script || "-"}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StoryboardTable;
